/* tslint:disable:max-line-length */
import loadable from '@loadable/component';
import SkeletonCharacterLayout from '../../../../../shared/frontend/components/character/SkeletonCharacterLayout';
import SkeletonGuildPages from '../../../../../shared/frontend/components/guild/SkeletonGuildPages';
import SkeletonRaidRankingsPage from '../../../../../shared/frontend/components/layouts/SkeletonRaidRankingsPage';
import { TRouteDef } from '../../../../../shared/frontend/libs/router/router-helpers';
import { TSiteConfigState } from '../../../../../wowretail/src/frontend/scripts/libs/store';
import App from '../components/presentational/app';
import NotFoundPage from '../../../../../shared/frontend/components/presentational/notFoundPage';
import { namedRoutes } from './routes';
import RootNode from './rootNode';

export function getRoutes(siteConfig: Pick<TSiteConfigState, 'currentRaidSlug' | 'currentRaidDifficulty'>) {
  const routes: TRouteDef[] = [
    {
      path: '/',
      children: [
        {
          from: '/home',
          to: '/',
          exact: true
        },
        {
          from: '/recruit',
          to: '/recruitment',
          exact: true
        },
        {
          from: '/u/:name',
          to: namedRoutes.UserProfile
        },
        {
          from: '/:raid/realms/:region/heroic',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/:raid/realms/:region/normal',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/:raid/realms/:region/mythic',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/realms/world',
          to: namedRoutes.ConnectedRealms,
          exact: true
        },
        { exact: true, from: '/add', to: '/' },
        { exact: true, from: '/add/success', to: '/' },

        //
        // Raiding
        //
        {
          exact: true,
          from: '/raids',
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },
        {
          exact: true,
          from: '/raids/:region/:realm',
          to: `/${siteConfig.currentRaidSlug}/rankings/:region/:realm/${siteConfig.currentRaidDifficulty}`
        },
        {
          exact: true,
          from: '/raid',
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },
        {
          exact: true,
          from: `/${siteConfig.currentRaidSlug}`,
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },
        {
          exact: true,
          from: '/character-rankings/otk-hardcore-makgora-level',
          to: '/character-rankings/otk-hardcore-makgora-level/world/all/all/all'
        }
      ]
    },
    {
      path: '/',
      component: App,
      children: [
        {
          path: namedRoutes.Home,
          component: loadable(() => import('../components/container/homePage')),
          index: true
        },

        {
          path: '/settings',
          component: loadable(() => import('../components/presentational/appSettings')),
          children: [
            //
            // Character Settings
            //
            {
              path: namedRoutes.UserSettings,
              component: loadable(() => import('../components/container/settings/userSettingsNavOrRedirect')),
              exact: true
            },
            {
              path: namedRoutes.UserBlockList,
              component: loadable(() => import('../components/container/settings/userBlockListPage'))
            },
            {
              path: namedRoutes.UserBattleNetSettings,
              component: loadable(() => import('../components/container/settings/userBattleNetSettingsPage')),
              exact: true
            },

            {
              path: namedRoutes.UserCharacterProfileSettings,
              component: loadable(() => import('../components/container/settings/userCharacterProfileSettingsPage'))
            },

            {
              path: namedRoutes.UserCharacterPrivacySettings,
              component: loadable(() => import('../components/container/settings/userCharacterPrivacySettingsPage')),
              exact: true
            },

            {
              path: namedRoutes.UserCharacterTransferSettings,
              component: loadable(() => import('../components/container/settings/userCharacterTransferSettingsPage')),
              exact: true
            },

            {
              path: namedRoutes.UserPremiumSettings,
              component: loadable(() => import('../components/container/settings/userPremiumSettingsPage'))
            },

            //
            // Group Settings
            //
            {
              path: namedRoutes.UserGroupSettings,
              component: loadable(() => import('../components/container/settings/userGroupSettingsPage')),
              exact: true
            },
            {
              path: namedRoutes.UserGroupRoleSettings,
              component: loadable(() => import('../components/container/settings/userGroupRoleSettingsPage')),
              exact: true
            },
            {
              path: namedRoutes.UserGroupPrivacySettings,
              component: loadable(
                () =>
                  import('../../../../../shared/frontend/components/settings/groupCharter/userGroupPrivacySettingsPage')
              ),
              exact: true
            },
            {
              path: namedRoutes.UserGroupClientUsage,
              component: loadable(() => import('../components/container/settings/userGroupClientUsagePage')),
              exact: true
            },
            {
              path: namedRoutes.UserGroupBlockListSettings,
              component: loadable(() => import('../components/container/settings/userGroupBlockListPage'))
            },

            // Recruitment Profiles
            // recruitment profile creation
            {
              path: namedRoutes.RecruitmentProfileCreate,
              component: loadable(() => import('../recruitmentProfiles/containers/recruitmentProfileCreation')),
              exact: true
            },
            {
              path: namedRoutes.UserRecruitmentProfiles,
              component: loadable(() => import('../recruitmentSettings/containers/recruitmentProfilesPage')),
              exact: true
            },
            // recruitment profile editing
            {
              path: namedRoutes.RecruitmentProfileConfig,
              component: loadable(() => import('../recruitmentProfiles/containers/recruitmentProfileEditing')),
              exact: true
            }
          ]
        },

        {
          path: namedRoutes.MessageCenterHomePage,
          component: loadable(() => import('../messageCenter/components/messageCenterLayout')),
          children: [
            {
              path: namedRoutes.MessageCenterHomePage,
              component: loadable(() => import('../messageCenter/containers/messageCenterHome')),
              exact: true
            },
            {
              path: namedRoutes.MessageCenterThread,
              component: loadable(() => import('../messageCenter/containers/messageCenterThread'))
            }
          ]
        },

        {
          path: '/guilds',
          component: loadable(() => import('../guild/containers/guildPageContainer')),
          children: [
            {
              component: loadable(() => import('../guild/containers/guildRaidCompositionPage')),
              path: namedRoutes.GuildRaidComposition,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildCharacterAchievementLeaderboardPage')),
              path: namedRoutes.GuildCharacterAchievementLeaderboard,
              fallback: SkeletonGuildPages,
              exact: true
            },
            {
              component: loadable(() => import('../guild/containers/guildRosterPage')),
              path: namedRoutes.GuildRoster,
              fallback: SkeletonGuildPages,
              exact: true
            },
            {
              component: loadable(() => import('../guild/containers/guildRecruitmentPage')),
              path: namedRoutes.GuildRecruitmentPage,
              exact: true
            },
            // this should come after the other guild pages since it has a more open route
            // and acts as a fallback
            {
              component: loadable(() => import('../guild/containers/guildDetailsPage')),
              path: namedRoutes.GuildDetails,
              fallback: SkeletonGuildPages
            }
          ]
        },

        {
          component: loadable(() => import('../components/container/raidHallOfFamePage')),
          path: namedRoutes.RaidHallOfFame
        },
        {
          component: loadable(() => import('../components/container/raidHallOfFamePage')),
          path: namedRoutes.RaidFactionHallOfFame
        },
        {
          component: loadable(() => import('../components/container/raidHallOfFamePage')),
          path: namedRoutes.RaidRealmHallOfFame
        },
        {
          component: loadable(() => import('../components/container/raidRankingsPage')),
          path: namedRoutes.RaidFactionRankings
        },
        {
          component: loadable(() => import('../components/container/raidRankingsPage')),
          path: namedRoutes.RaidFactionRealmRankings
        },
        {
          component: loadable(() => import('../components/container/raidRankingsPage')),
          path: namedRoutes.RaidRankings,
          fallback: SkeletonRaidRankingsPage
        },
        {
          component: loadable(() => import('../components/container/raidRankingsPage')),
          path: namedRoutes.RaidRealmRankings
        },
        {
          component: loadable(() => import('../components/container/raidWorldFirstTrackerPage')),
          path: namedRoutes.RaidWorldFirstTracker,
          exact: true
        },
        {
          component: loadable(() => import('../components/container/raidWorldFirstAnalyzePage')),
          path: namedRoutes.RWFAnalyzerPage,
          exact: true
        },
        {
          component: loadable(() => import('../components/container/bossRankingsPage')),
          path: namedRoutes.BossRankings
        },
        {
          component: loadable(() => import('../components/container/bossRankingsPage')),
          path: namedRoutes.BossFactionRankings
        },
        {
          component: loadable(() => import('../components/container/bossRankingsPage')),
          path: namedRoutes.BossRealmRankings
        },
        {
          component: loadable(() => import('../components/container/bossRankingsPage')),
          path: namedRoutes.BossFactionRealmRankings
        },
        {
          component: loadable(() => import('../components/container/progressRankingsPage')),
          path: namedRoutes.ProgressRankings
        },
        {
          component: loadable(() => import('../components/container/progressRankingsPage')),
          path: namedRoutes.ProgressFactionRankings
        },
        {
          component: loadable(() => import('../components/container/progressRankingsPage')),
          path: namedRoutes.ProgressRealmRankings
        },
        {
          component: loadable(() => import('../components/container/progressRankingsPage')),
          path: namedRoutes.ProgressFactionRealmRankings
        },

        {
          component: loadable(() => import('../character/containers/CharacterRecruitmentPage')),
          path: namedRoutes.CharacterRecruitmentPage,
          exact: true
        },
        {
          component: loadable(() => import('../character/containers/detailsPage')),
          path: namedRoutes.CharacterDetails,
          fallback: SkeletonCharacterLayout
        },
        {
          component: loadable(() => import('../character/containers/snapshotPage')),
          path: namedRoutes.CharacterSnapshot
        },

        {
          component: loadable(() => import('../components/container/characterRankingsPage')),
          path: namedRoutes.CharacterRankings
        },

        {
          component: loadable(() => import('../guild/containers/snapshotPage')),
          path: namedRoutes.GuildSnapshot
        },

        {
          component: loadable(() => import('../components/presentational/recruitmentPage')),
          path: namedRoutes.Recruitment
        },
        {
          path: namedRoutes.RealmHome,
          component: loadable(() => import('../components/container/specificHomePage')),
          exact: true
        },
        {
          path: namedRoutes.RegionHome,
          component: loadable(() => import('../components/container/specificHomePage')),
          exact: true
        },
        {
          component: loadable(() => import('../components/container/connectedRealmsPage')),
          path: namedRoutes.ConnectedRealms
        },
        {
          component: loadable(() => import('../components/presentational/raidLiveEncounter')),
          path: namedRoutes.LiveLoggedEncounterList
        },
        {
          component: loadable(() => import('../components/presentational/raidLoggedEncounterPage')),
          path: namedRoutes.RaidLoggedEncounter
        },

        // search results
        {
          component: loadable(() => import('../GlobalSearch/containers/SearchResultsPage')),
          path: namedRoutes.Search
        },

        // user profile
        {
          component: loadable(() => import('../user/containers/userProfilePage')),
          path: namedRoutes.UserProfile
        },

        // Events
        // OTK Makgora
        {
          component: loadable(() => import('../events/otk-makgora/registrationPage')),
          path: namedRoutes.OTKMakgoraRegistrationPage,
          exact: true
        },

        // Not Found Page
        {
          component: NotFoundPage,
          path: '/*'
        }
      ]
    },

    //
    // Fullscreen Routes
    //
    {
      component: loadable(() => import('../components/container/loginPage')),
      path: namedRoutes.Login
    },

    // Stream Embed
    {
      component: loadable(() => import('../streamWidget/containers/bossPullsFeed')),
      path: namedRoutes.StreamEmbedBossPullsFeedWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/bossProgress')),
      path: namedRoutes.StreamEmbedBossProgressWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/bossProgressGallery')),
      path: namedRoutes.StreamEmbedBossProgressWidgetGallery,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/rwfEventFeed')),
      path: namedRoutes.StreamEmbedRwfEventFeedWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/healthOverAttempt')),
      path: namedRoutes.StreamEmbedHealthOverAttemptWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/versusWidget')),
      path: namedRoutes.StreamEmbedVersusWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/versusTimelineWidget')),
      path: namedRoutes.StreamEmbedVersusTimelineWidget,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/rwfGuildLoot')),
      path: namedRoutes.StreamEmbedRwfGuildLoot,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/compBreakdown')),
      path: namedRoutes.StreamEmbedCompBreakdown,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/guildGearComparison')),
      path: namedRoutes.StreamEmbedGuildGearComparison,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/guildPullsComparison')),
      path: namedRoutes.StreamEmbedGuildPullsComparison,
      exact: true
    },
    {
      component: loadable(() => import('../streamWidget/containers/raidLeaderboard')),
      path: namedRoutes.StreamEmbedRaidLeaderboardWidget,
      exact: true
    }
  ];

  const withRootNode = [
    {
      component: RootNode,
      children: routes,
      path: '/'
    }
  ];

  return withRootNode;
}
